// Small pieces shared by cards and records: badges, labels, checks, review, column names.
import type { Card, Checks as ChecksT, ColumnKey, PullSummary } from '../api/client';
import { Icon, type IconName } from './Icon';
import { cn } from './ui';
import { labelTone, type LabelRoles } from '../lib/labels';

/** Display names, matching the API's `columns[].name` (used where no board is loaded, e.g. a record). */
export const COLUMN_NAMES: Record<ColumnKey, string> = {
  inbox: 'Inbox',
  exploring: 'Exploring',
  locked: 'Locked',
  inflight: 'In flight',
  review: 'In review',
  shipped: 'Shipped',
};
export const COLUMN_ORDER: ColumnKey[] = ['inbox', 'exploring', 'locked', 'inflight', 'review', 'shipped'];

export const KIND_ICON: Record<Card['kind'], IconName> = {
  plan: 'plan',
  batch: 'batch',
  chore: 'chore',
  bug: 'bug',
  inbox: 'idea',
  pr: 'pr',
  unparsed: 'warn',
};

export const KIND_NAME: Record<Card['kind'], string> = {
  plan: 'Plan',
  batch: 'Bug batch',
  chore: 'Chore',
  bug: 'Bug',
  inbox: 'Inbox note',
  pr: 'Unlinked PR',
  unparsed: 'Unparsed file',
};

type Badge = Card['badges'][number];

// A tag's look by tone. The tone name is kept as a hook class too (`tag label-bug`): tests and the
// record's field list find tags by it.
const TAG_TONE = {
  plain: 'bg-badge-hold-bg text-badge-hold-ink',
  kind: 'bg-transparent border border-hairline',
  owner: 'bg-transparent border border-hairline',
  'label-plain': 'bg-badge-hold-bg text-badge-hold-ink',
  'badge-duplicate': 'bg-badge-hold-bg text-badge-hold-ink',
  'badge-parked': 'bg-badge-hold-bg text-badge-hold-ink',
  'badge-draft': 'bg-badge-hold-bg text-badge-hold-ink',
  'badge-hold': 'bg-badge-hold-bg text-badge-hold-ink border border-dashed border-hairline-strong',
  'label-hold': 'bg-badge-hold-bg text-badge-hold-ink border border-dashed border-hairline-strong',
  'badge-needs-report': 'bg-badge-needs-report-bg text-badge-needs-report-ink font-semibold',
  'badge-unparsed': 'bg-badge-unparsed-bg text-badge-unparsed-ink font-semibold',
  'badge-release': 'bg-status-review-faded text-status-review-ink',
  'label-bug': 'bg-label-bug-bg text-label-bug-ink',
  'label-triage': 'bg-label-triage-bg text-label-triage-ink',
  'label-epic': 'bg-label-epic-bg text-label-epic-ink',
  'badge-batch': 'relative z-[1] bg-badge-batch-bg text-badge-batch-ink font-semibold hover:underline coarse:h-6',
} as const;
export type TagTone = keyof typeof TAG_TONE;

/** The board's tag (a badge, a label, a kind): small, on its tone's fill. */
export function tagClass(tone: TagTone, className?: string): string {
  return cn(
    'tag inline-flex h-[19px] items-center gap-1 whitespace-nowrap rounded-[5px] px-1.5 text-[11px] font-medium',
    tone === 'badge-batch' || tone === 'owner' ? 'font-mono' : 'font-sans',
    tone === 'plain' ? '' : tone,
    TAG_TONE[tone],
    className,
  );
}

const BADGE_TEXT: Record<Badge, string> = {
  'needs-report': 'Merged — needs report',
  hold: 'hold',
  duplicate: 'duplicate',
  unparsed: 'Unparsed file',
  parked: 'parked',
  release: 'release',
  draft: 'draft',
};

export function BadgeTag({ badge }: { badge: Badge }) {
  return (
    <span className={tagClass(`badge-${badge}`)} data-badge={badge}>
      {badge === 'hold' && <Icon name="pause" small />}
      {badge === 'unparsed' && <Icon name="warn" small />}
      {BADGE_TEXT[badge]}
    </span>
  );
}

/** A label, coloured by its role in the meta repo (bug, needs_triage, hold); others are neutral. */
export function LabelTag({ name, roles }: { name: string; roles: LabelRoles | undefined }) {
  return <span className={tagClass(labelTone(name, roles))}>{name}</span>;
}

export function EpicTag({ epic }: { epic: string }) {
  return (
    <span className={tagClass('label-epic')} title="Epic">
      {epic}
    </span>
  );
}

/** Each column's colours: its pill (faded fill, ink), its hue (dots, rules), as classes. */
export const COLUMN_TONE: Record<ColumnKey, { pill: string; hue: string; border: string }> = {
  inbox: { pill: 'bg-status-inbox-faded text-status-inbox-ink', hue: 'bg-status-inbox', border: 'border-status-inbox' },
  exploring: {
    pill: 'bg-status-exploring-faded text-status-exploring-ink',
    hue: 'bg-status-exploring',
    border: 'border-status-exploring',
  },
  locked: { pill: 'bg-status-locked-faded text-status-locked-ink', hue: 'bg-status-locked', border: 'border-status-locked' },
  inflight: {
    pill: 'bg-status-inflight-faded text-status-inflight-ink',
    hue: 'bg-status-inflight',
    border: 'border-status-inflight',
  },
  review: { pill: 'bg-status-review-faded text-status-review-ink', hue: 'bg-status-review', border: 'border-status-review' },
  shipped: {
    pill: 'bg-status-shipped-faded text-status-shipped-ink',
    hue: 'bg-status-shipped',
    border: 'border-status-shipped',
  },
};

const PILL = 'status-pill inline-flex items-center gap-1.5 rounded-[6px] py-0.5 pl-1.5 pr-2 text-[11.5px] font-semibold';

export function StatusPill({ column }: { column: ColumnKey | null }) {
  if (!column) return <span className={cn(PILL, 'border border-dashed border-hairline-strong')}>Hidden (dropped)</span>;
  return (
    <span className={cn(PILL, COLUMN_TONE[column].pill)}>
      <span className={cn('size-1.5 rounded-full', COLUMN_TONE[column].hue)} aria-hidden="true" />
      {COLUMN_NAMES[column]}
    </span>
  );
}

/** One dot per run: pass filled, fail a cross, pending a ring. Shape carries the state, not hue alone. */
export function RunMark({ state }: { state: 'pass' | 'fail' | 'pending' }) {
  if (state === 'fail') {
    return (
      <svg className="mark-fail size-2.5 flex-none fill-none stroke-current text-checks-fail [stroke-width:2.4]" viewBox="0 0 16 16" aria-hidden="true">
        <path d="m3.5 3.5 9 9M12.5 3.5l-9 9" />
      </svg>
    );
  }
  return (
    <span
      className={cn(
        'inline-block size-[7px] flex-none rounded-full',
        state === 'pass' ? 'bg-checks-pass' : 'bg-transparent shadow-[inset_0_0_0_1.6px_var(--checks-pending)]',
      )}
      aria-hidden="true"
    />
  );
}

/** A sync state's dot: ok filled, pending a ring, error a square (shape, not hue alone). */
export function pulseClass(state: 'ok' | 'pending' | 'error'): string {
  return cn(
    'size-[7px] flex-none',
    state === 'ok' && 'rounded-full bg-checks-pass',
    state === 'pending' && 'rounded-full bg-transparent shadow-[inset_0_0_0_1.6px_var(--checks-pending)]',
    state === 'error' && 'rounded-[1px] bg-checks-fail',
  );
}

export function checksText(c: ChecksT): string {
  switch (c.state) {
    case 'pass':
      return c.total === 1 ? 'check passed' : `${c.total} checks passed`;
    case 'fail':
      return `${c.failed} failing`;
    case 'pending':
      return `${c.pending} pending`;
    default:
      return 'no checks';
  }
}

const CHECKS_TEXT: Record<ChecksT['state'], string> = {
  pass: 'text-checks-pass-ink',
  fail: 'text-checks-fail-ink font-semibold',
  pending: 'text-checks-pending-ink',
  none: '',
};

/** A PR's checks summary: up to five marks and the words (fail is a cross and text, never colour alone). */
export function ChecksSummary({ checks }: { checks: ChecksT }) {
  const marks = checks.runs.slice(0, 5);
  return (
    <span className="checks-sum inline-flex items-center gap-1.5 whitespace-nowrap" data-checks={checks.state}>
      {marks.length > 0 && (
        <span className="inline-flex items-center gap-[3px]" aria-hidden="true">
          {marks.map((r, i) => (
            <RunMark key={i} state={r.state} />
          ))}
        </span>
      )}
      <span className={CHECKS_TEXT[checks.state]}>
        {checks.state === 'fail' && <span className="sr-only">Checks failing: </span>}
        {checksText(checks)}
      </span>
    </span>
  );
}

const REVIEW_TEXT: Record<PullSummary['review'], string> = {
  approved: 'approved',
  changes_requested: 'changes requested',
  commented: 'commented',
  none: 'review required',
};

const REVIEW_TONE: Record<PullSummary['review'], string> = {
  approved: 'text-checks-pass-ink',
  changes_requested: 'text-checks-fail-ink',
  commented: '',
  none: '',
};

export function ReviewText({ review, className }: { review: PullSummary['review']; className?: string }) {
  return (
    <span className={cn('whitespace-nowrap', REVIEW_TONE[review], className)} data-review={review}>
      {REVIEW_TEXT[review]}
    </span>
  );
}

export function prStateText(pr: PullSummary): string {
  if (pr.state === 'merged') return 'merged';
  if (pr.state === 'closed') return 'closed';
  return pr.draft ? 'draft' : 'open';
}
